import { convertMLToLiter } from "../convertUnits";

export const MeterUploadRowStatus = {
  Draft: "Draft",
  Success: "Success",
  Failed: "Failed",
} as const;
export type MeterUploadRowStatusValue =
  (typeof MeterUploadRowStatus)[keyof typeof MeterUploadRowStatus];

export type MeterUploadRowData = {
  accountNumber: string;
  extractionPointName: string;
  serialNo?: string;
  readAt: string;
  reading: number;
  group?: string;
  seq?: number;
  // Free-text note from the spreadsheet's Note column; becomes a Note on the row's declaration.
  note?: string;
};

export type ManualIntervention = {
  calculation: string;
  volumeExtracted: number;
};

export type MeterUploadRow = {
  id: string;
  rowData: MeterUploadRowData;
  manualIntervention?: ManualIntervention;
  status: MeterUploadRowStatusValue;
  failureReasonCode?: string;
  failureReasonDetail?: string;
  // Set server-side once the row ledgers: the id of the declaration the reading
  // became. The row's spreadsheet note is persisted as a declaration note anchored
  // to (DBTables.Declarations, declarationId), so this is the reference the ledgered
  // row's note cell uses to open the real, read-only note view. Null/undefined until
  // the row is ledgered.
  declarationId?: string | null;
};

export type TrialRunServerRow = {
  group: string;
  seq: number;
  accountNumber: string;
  subscriberName: string;
  extractionPointName: string;
  serialNo: string;
  lastReading: number;
  readAt: string;
  volumeExtracted: number;
  clickOver: number;
  isActive: boolean;
  isDuplicate: boolean;
  requireManualIntervention: boolean;
};

export type TrialRunRowErrors = {
  group: boolean;
  seq: boolean;
  accountNumber: boolean;
  extractionPointName: boolean;
  extractionPointInactive: boolean;
  extractionPointNotFound: boolean;
  serialNo: boolean;
  duplicate: boolean;
  requireManualIntervention: boolean;
  clickOver: boolean;
};

export const TrialRunDisplayStatus = {
  Success: "success",
  Warning: "warning",
  Error: "error",
  Ledgered: "ledgered",
  Failed: "failed",
} as const;
export type TrialRunDisplayStatusValue =
  (typeof TrialRunDisplayStatus)[keyof typeof TrialRunDisplayStatus];

export type TrialRunDisplayRow = {
  id: string;
  rowData: MeterUploadRowData;
  status: MeterUploadRowStatusValue;
  failureReasonCode?: string;
  failureReasonDetail?: string;
  // Carried through from the row so a ledgered row's note cell can open the real
  // declaration note (see MeterUploadRow.declarationId). Null/undefined until ledgered.
  declarationId?: string | null;
  trial: {
    subscriberName: string;
    lastReading: number;
    volumeExtracted: number;
    clickOver: number;
    readAt: string;
    calculation: string;
    // The offtake's actual group/seq (server truth) so the detail modal's "Ledger
    // details" column can show them distinct from the uploaded file value.
    group: string;
    seq: number;
  };
  errors: TrialRunRowErrors;
  displayStatus: TrialRunDisplayStatusValue;
};

// A row with no server result yet (e.g. a freshly inserted inline row, or a row
// held back from the trial run because its identity fields aren't filled in). An
// empty extractionPointName makes computeErrors flag it as not-found, so it renders
// flagged rather than as a phantom success.
export const EMPTY_TRIAL_SERVER_ROW: TrialRunServerRow = {
  group: "",
  seq: 0,
  accountNumber: "",
  subscriberName: "",
  extractionPointName: "",
  serialNo: "",
  lastReading: 0,
  readAt: "",
  volumeExtracted: 0,
  clickOver: 0,
  isActive: false,
  isDuplicate: false,
  requireManualIntervention: false,
};

const noErrors: TrialRunRowErrors = {
  group: false,
  seq: false,
  accountNumber: false,
  extractionPointName: false,
  extractionPointInactive: false,
  extractionPointNotFound: false,
  serialNo: false,
  duplicate: false,
  requireManualIntervention: false,
  clickOver: false,
};

// Group is a free-text scheme code (e.g. "MV1") that a spreadsheet often carries with
// cosmetic differences — trailing spaces, lower case — that mean the same group. Fold
// those away so only a genuinely different group warns (and so the offtake cascade
// doesn't ask to "overwrite" a group that already matches; see cascadeWouldOverwrite).
export const normalizeGroup = (group: string): string =>
  group.trim().toLowerCase();

const computeErrors = (
  row: MeterUploadRow,
  trial: TrialRunServerRow,
): TrialRunRowErrors => {
  if (!trial.extractionPointName) {
    return { ...noErrors, extractionPointNotFound: true };
  }

  return {
    ...noErrors,
    group: normalizeGroup(trial.group) !== normalizeGroup(row.rowData.group ?? ""),
    // seq is a decimal(10,3) value that can reach either side as a string ("8.000")
    // or a number. Compare numerically so a type difference alone never reads as a
    // mismatch — only a genuinely different sequence warns.
    seq: Number(trial.seq ?? 0) !== Number(row.rowData.seq ?? 0),
    accountNumber: trial.accountNumber !== row.rowData.accountNumber,
    extractionPointName:
      trial.extractionPointName !== row.rowData.extractionPointName,
    serialNo: trial.serialNo !== (row.rowData.serialNo ?? ""),
    extractionPointInactive: !trial.isActive,
    duplicate: trial.isDuplicate,
    requireManualIntervention:
      trial.requireManualIntervention && !row.manualIntervention,
    clickOver:
      trial.clickOver > 0 &&
      convertMLToLiter(row.rowData.reading) > trial.clickOver,
  };
};

export const buildTrialRunRow = ({
  row,
  trialResult = EMPTY_TRIAL_SERVER_ROW,
}: {
  row: MeterUploadRow;
  // Optional: a not-yet-validated row falls back to an empty result and renders flagged.
  trialResult?: TrialRunServerRow;
}): TrialRunDisplayRow => {
  if (row.status === MeterUploadRowStatus.Success) {
    return {
      id: row.id,
      rowData: row.rowData,
      status: row.status,
      failureReasonCode: row.failureReasonCode,
      failureReasonDetail: row.failureReasonDetail,
      declarationId: row.declarationId,
      trial: {
        subscriberName: trialResult.subscriberName,
        lastReading: trialResult.lastReading,
        volumeExtracted: trialResult.volumeExtracted,
        clickOver: trialResult.clickOver,
        readAt: trialResult.readAt,
        calculation: row.manualIntervention?.calculation ?? "",
        group: trialResult.group,
        seq: Number(trialResult.seq ?? 0),
      },
      errors: { ...noErrors },
      displayStatus: TrialRunDisplayStatus.Ledgered,
    };
  }

  const errors = computeErrors(row, trialResult);

  const volumeExtracted = row.manualIntervention
    ? row.manualIntervention.volumeExtracted
    : errors.extractionPointName || errors.serialNo
      ? 0
      : trialResult.volumeExtracted;

  const calculation = row.manualIntervention?.calculation ?? "";

  const hasError =
    errors.extractionPointNotFound ||
    errors.accountNumber ||
    errors.extractionPointName ||
    errors.serialNo ||
    errors.extractionPointInactive ||
    errors.duplicate ||
    errors.requireManualIntervention ||
    errors.clickOver;

  const hasWarning = errors.group || errors.seq;

  const displayStatus: TrialRunDisplayStatusValue = hasError
    ? TrialRunDisplayStatus.Error
    : hasWarning
      ? TrialRunDisplayStatus.Warning
      : row.status === MeterUploadRowStatus.Failed
        ? TrialRunDisplayStatus.Failed
        : TrialRunDisplayStatus.Success;

  return {
    id: row.id,
    rowData: row.rowData,
    status: row.status,
    failureReasonCode: row.failureReasonCode,
    failureReasonDetail: row.failureReasonDetail,
    declarationId: row.declarationId,
    trial: {
      subscriberName: trialResult.subscriberName,
      lastReading: trialResult.lastReading,
      volumeExtracted,
      clickOver: trialResult.clickOver,
      readAt: trialResult.readAt,
      calculation,
      group: trialResult.group,
      seq: Number(trialResult.seq ?? 0),
    },
    errors,
    displayStatus,
  };
};
